import { useCallback, useEffect, useState } from 'react';
import { BellRing, BellOff, Smartphone, ShieldCheck, X } from 'lucide-react';
import apiConfig from '../apiConfig';
import { syncUnreadAppBadge } from '../utils/appBadge';
import { getCustomerAuthHeaders } from '../utils/auth/customerAuthToken';

type PushConfigResponse = {
  enabled?: boolean;
  public_key?: string | null;
};

const AUTO_PROMPT_SESSION_KEY = 'customer_push_prompt_seen_in_session_v1';
const AUTO_PROMPT_DELAY_MS = 650;
const SERVICE_WORKER_TIMEOUT_MS = 10000;
const REQUEST_TIMEOUT_MS = 10000;

type PushNotificationCardProps = {
  mode?: 'settings' | 'notice';
  onOpenSettings?: () => void;
};

const detectPushSupport = (): boolean => {
  try {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  } catch {
    return false;
  }
};

const getNotificationPermissionSafely = (): NotificationPermission => {
  try {
    if ('Notification' in window && typeof Notification.permission === 'string') {
      return Notification.permission;
    }
  } catch {
    return 'default';
  }

  return 'default';
};

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }
  return outputArray;
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
  let timer = 0;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => {
          reject(new Error(`${label} timed out`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
};

const fetchWithTimeout = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(input, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
};

const PushNotificationCard = ({
  mode = 'settings',
  onOpenSettings,
}: PushNotificationCardProps) => {
  const [supported, setSupported] = useState<boolean>(false);
  const [config, setConfig] = useState<PushConfigResponse | null>(null);
  const [configLoaded, setConfigLoaded] = useState<boolean>(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscriptionActive, setSubscriptionActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [showAutoPrompt, setShowAutoPrompt] = useState<boolean>(false);

  useEffect(() => {
    const nextSupported = detectPushSupport();
    setSupported(nextSupported);
    setPermission(nextSupported ? getNotificationPermissionSafely() : 'denied');
  }, []);

  const fetchConfig = useCallback(async (): Promise<void> => {
    if (!supported) return;
    try {
      const res = await fetchWithTimeout(`${apiConfig.baseURL}/api/customer-app/push-subscriptions/config`);
      if (!res.ok) {
        setMessage('Notifications are not available right now. Please try again later.');
        return;
      }
      const data = (await res.json()) as PushConfigResponse;
      setConfig(data);
      if (!data.enabled || !data.public_key) {
        setMessage('Notifications are not available right now. Please try again later.');
      }
    } catch (error) {
      console.error(error);
      setMessage('Notifications are not available right now. Please try again later.');
    } finally {
      setConfigLoaded(true);
    }
  }, [supported]);

  const getServiceWorkerRegistration = useCallback(async (): Promise<ServiceWorkerRegistration> => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    let registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      registration = await navigator.serviceWorker.register(swUrl);
    }

    return withTimeout(
      navigator.serviceWorker.ready.then((readyRegistration) => readyRegistration || (registration as ServiceWorkerRegistration)),
      SERVICE_WORKER_TIMEOUT_MS,
      'Service worker setup',
    );
  }, []);

  const syncSubscription = useCallback(async (): Promise<void> => {
    if (!supported || !config?.enabled || !config.public_key || getNotificationPermissionSafely() !== 'granted') {
      setSubscriptionActive(false);
      return;
    }

    try {
      const registration = await getServiceWorkerRegistration();
      const existing = await registration.pushManager.getSubscription();
      if (!existing) {
        setSubscriptionActive(false);
        return;
      }

      const json = existing.toJSON();
      await fetchWithTimeout(`${apiConfig.baseURL}/api/customer-app/push-subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCustomerAuthHeaders(),
        },
        body: JSON.stringify({
          subscription: {
            endpoint: json.endpoint ?? existing.endpoint,
            keys: {
              p256dh: json.keys?.p256dh ?? '',
              auth: json.keys?.auth ?? '',
            },
          },
          user_agent: navigator.userAgent,
        }),
      });
      setSubscriptionActive(true);
    } catch (error) {
      console.error(error);
      setMessage('This device is taking too long to prepare notifications. Please reopen the app and try again.');
      setSubscriptionActive(false);
    }
  }, [config?.enabled, config?.public_key, getServiceWorkerRegistration, supported]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    setPermission(supported ? getNotificationPermissionSafely() : 'denied');
    if (getNotificationPermissionSafely() === 'granted') {
      void syncSubscription();
    }
  }, [supported, syncSubscription]);

  const dismissAutoPrompt = useCallback((): void => {
    setShowAutoPrompt(false);
    try {
      window.sessionStorage.setItem(AUTO_PROMPT_SESSION_KEY, '1');
    } catch {
      // Ignore storage access issues and continue without persistence.
    }
  }, []);

  useEffect(() => {
    const canEnable = Boolean(config?.enabled && config?.public_key);
    const shouldPrompt = supported
      && configLoaded
      && canEnable
      && permission === 'default'
      && !subscriptionActive;

    if (!shouldPrompt) {
      setShowAutoPrompt(false);
      return;
    }

    try {
      if (window.sessionStorage.getItem(AUTO_PROMPT_SESSION_KEY) === '1') {
        return;
      }
    } catch {
      // If session storage is unavailable we still show the prompt once.
    }

    const timer = window.setTimeout(() => {
      setShowAutoPrompt(true);
    }, AUTO_PROMPT_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    config?.enabled,
    config?.public_key,
    configLoaded,
    permission,
    subscriptionActive,
    supported,
  ]);

  const enableNotifications = useCallback(async (): Promise<boolean> => {
    if (!supported || !config?.enabled || !config.public_key) return false;
    setLoading(true);
    setMessage('');

    try {
      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);

      if (nextPermission !== 'granted') {
        setSubscriptionActive(false);
        setMessage(nextPermission === 'denied'
          ? 'Notifications are blocked. Please allow them in your browser settings.'
          : 'Notification permission was not enabled.');
        dismissAutoPrompt();
        return false;
      }

      const registration = await getServiceWorkerRegistration();
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription = await withTimeout(
          registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(config.public_key),
          }),
          SERVICE_WORKER_TIMEOUT_MS,
          'Push subscription',
        );
      }

      const json = subscription.toJSON();
      const res = await fetchWithTimeout(`${apiConfig.baseURL}/api/customer-app/push-subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCustomerAuthHeaders(),
        },
        body: JSON.stringify({
          subscription: {
            endpoint: json.endpoint ?? subscription.endpoint,
            keys: {
              p256dh: json.keys?.p256dh ?? '',
              auth: json.keys?.auth ?? '',
            },
          },
          user_agent: navigator.userAgent,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to save push subscription');
      }

      setSubscriptionActive(true);
      setMessage('Notifications are on for this device.');
      void syncUnreadAppBadge();
      dismissAutoPrompt();
      return true;
    } catch (error) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : '';
      setMessage(
        errorMessage.includes('timed out')
          ? 'Notification setup took too long on this device. Please reopen the app and try again.'
          : 'Could not enable notifications on this device.',
      );
      return false;
    } finally {
      setLoading(false);
    }
  }, [config?.enabled, config?.public_key, dismissAutoPrompt, getServiceWorkerRegistration, supported]);

  const disableNotifications = useCallback(async (): Promise<void> => {
    if (!supported) return;
    setLoading(true);
    setMessage('');

    try {
      const registration = await getServiceWorkerRegistration();
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await fetchWithTimeout(`${apiConfig.baseURL}/api/customer-app/push-subscriptions`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...getCustomerAuthHeaders(),
          },
          body: JSON.stringify({
            endpoint,
          }),
        });
        await subscription.unsubscribe();
      }

      setSubscriptionActive(false);
      setMessage('Notifications are turned off on this device.');
    } catch (error) {
      console.error(error);
      setMessage('Could not turn off notifications.');
    } finally {
      setLoading(false);
    }
  }, [getServiceWorkerRegistration, supported]);

  if (!supported) return null;

  const showEnable = permission !== 'granted' || !subscriptionActive;
  const canEnable = Boolean(config?.enabled && config?.public_key);
  const statusLabel = !canEnable
    ? 'Unavailable right now'
    : showEnable
    ? 'Off on this device'
    : 'On on this device';

  if (mode === 'notice') {
    if (!showEnable) return null;

    return (
      <>
        <section className="rounded-[28px] border border-sky-100 bg-white/95 p-4 shadow-sm shadow-sky-100/60">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-[11px] font-semibold text-sky-700">
                <Smartphone className="h-3.5 w-3.5" />
                Notifications off
              </div>
              <div className="mt-3 text-sm font-semibold text-slate-900">
                Turn on reminders and store messages
              </div>
            </div>

            <button
              type="button"
              onClick={onOpenSettings}
              className="min-h-[44px] shrink-0 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            >
              Open settings
            </button>
          </div>

          {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
        </section>

        {showAutoPrompt ? (
          <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/45 p-3 sm:items-center">
            <div className="w-full max-w-md overflow-hidden rounded-[30px] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.28)]">
              <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-5 py-4 text-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">
                      Message alerts
                    </div>
                    <h3 className="mt-1 text-xl font-semibold">Turn on notifications for this device</h3>
                  </div>
                  <button
                    type="button"
                    onClick={dismissAutoPrompt}
                    className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                    aria-label="Close notification prompt"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-4 px-4 py-4">
                <p className="text-sm leading-6 text-slate-600">
                  Open Settings to turn on booking reminders and store messages.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    dismissAutoPrompt();
                    onOpenSettings?.();
                  }}
                  className="flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white"
                >
                  Open settings
                </button>

                <button
                  type="button"
                  onClick={dismissAutoPrompt}
                  className="flex min-h-[48px] w-full items-center justify-center rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <section className="rounded-[28px] border border-sky-100 bg-white/95 p-4 shadow-sm shadow-sky-100/60">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-[11px] font-semibold text-sky-700">
                <Smartphone className="h-3.5 w-3.5" />
                Device notifications
              </div>
              {showEnable && canEnable ? (
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Recommended
                </div>
              ) : null}
            </div>
            <h2 className="mt-3 text-lg font-semibold text-slate-900">Turn on reminders and store messages</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Turn notifications on to receive booking reminders and new store messages on this device.
            </p>
          </div>
          <div className={`rounded-2xl p-3 ${showEnable ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
            {showEnable ? <BellOff className="h-5 w-5" /> : <BellRing className="h-5 w-5" />}
          </div>
        </div>

        {showEnable && canEnable ? (
          <div className="mt-4 rounded-[24px] border border-emerald-200 bg-emerald-50/80 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
              Quick setup
            </div>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              <div className="rounded-2xl bg-white px-3 py-2">1. Tap the button below.</div>
              <div className="rounded-2xl bg-white px-3 py-2">2. Tap Allow in the next popup.</div>
            </div>
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {showEnable ? (
            <button
              type="button"
              onClick={() => {
                void enableNotifications();
              }}
              disabled={loading || !canEnable}
              className="min-h-[48px] rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Turning on...' : configLoaded && !canEnable ? 'Unavailable right now' : 'Turn on notifications'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void disableNotifications()}
              disabled={loading}
              className="min-h-[48px] rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Updating...' : 'Turn off on this device'}
            </button>
          )}

          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
            !canEnable
              ? 'bg-amber-100 text-amber-700'
              : showEnable
              ? 'bg-slate-100 text-slate-600'
              : 'bg-emerald-100 text-emerald-700'
          }`}>
            {statusLabel}
          </span>
        </div>

        {permission === 'denied' ? (
          <p className="mt-3 text-sm text-amber-700">
            Notifications are blocked in your browser or device settings. Please allow them there first.
          </p>
        ) : null}

        {configLoaded && !canEnable ? (
          <p className="mt-3 text-sm text-amber-700">
            Notifications cannot be turned on right now. Please try again later.
          </p>
        ) : null}

        {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
      </section>

      {showAutoPrompt ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/45 p-3 sm:items-center">
          <div className="w-full max-w-md overflow-hidden rounded-[30px] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.28)]">
            <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-5 py-4 text-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">
                    Message alerts
                  </div>
                  <h3 className="mt-1 text-xl font-semibold">Turn on notifications for this device</h3>
                </div>
                <button
                  type="button"
                  onClick={dismissAutoPrompt}
                  className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                  aria-label="Close notification prompt"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4 px-4 py-4">
              <p className="text-sm leading-6 text-slate-600">
                Get booking reminders and store messages without needing to keep this screen open.
              </p>

              <div className="rounded-[24px] border border-emerald-200 bg-emerald-50/80 p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-white p-2.5 text-emerald-700">
                    <BellRing className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">It only takes a moment</div>
                    <div className="mt-1 text-sm leading-6 text-slate-600">
                      Tap the button below, then tap <span className="font-semibold text-slate-900">Allow</span> in the next popup.
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  void enableNotifications();
                }}
                disabled={loading || !canEnable}
                className="flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Turning on...' : 'Turn on notifications'}
              </button>

              <button
                type="button"
                onClick={dismissAutoPrompt}
                className="flex min-h-[48px] w-full items-center justify-center rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PushNotificationCard;
